import { StarFilled, StarOutlined } from "@ant-design/icons";
import { message, Rate } from "antd";
import { useState } from "react";

import { IconGiveFeedback, IconX } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { BTText } from "@btnet/core-components/BTText";
import { BTTextArea } from "@btnet/core-components/BTTextArea";
import { useFeedbackSubmitPostV2 } from "@btnet/services-btnet/handlers/feedback/feedback";
import type { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";

import {
    BdsActionBar,
    BdsButton,
    BdsButtonGroup,
    BdsContentHeader,
} from "@buildertrend/components";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import yup from "utilities/form/yup";

import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";

import "./FeedbackWidget.less";

interface IFeedbackWidgetProps {
    feedbackTarget: FeedbackTarget;
    ratingMode?: "hidden" | "optional" | "required";
    onClose: () => void;
    onSubmitSuccess?: () => void;
    extraInfo?: string;
    feedbackText?: string;
}

export interface IFeedbackWidgetFormValues {
    rating: number;
    feedbackText: string;
}

function FeedbackWidgetInternal(
    props: IBTInjectedFormikProps<IFeedbackWidgetProps, IFeedbackWidgetFormValues>,
) {
    const {
        ratingMode = "optional",
        onClose,
        onSubmitSuccess,
        values,
        setFieldValue,
        setFieldTouched,
        isValid,
        feedbackTarget,
        extraInfo,
    } = props;
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const postFeedbackData = useFeedbackSubmitPostV2({
        mutation: {
            onError: (error) => {
                showApixErrorMessage(error);
            },
            onSuccess: () => {
                void message.success("Thank you for your feedback!", 5);
                onSubmitSuccess?.();
                onClose();
            },
        },
    });

    const handleSubmit = async () => {
        setIsSubmitting(true);
        await postFeedbackData.mutateAsync({
            data: {
                feedbackText: values.feedbackText,
                rating: values.rating,
                feedbackTarget: feedbackTarget,
                extraInfo: extraInfo,
            },
        });
    };
    const feedbackFooter = (
        <div className="flex flex-row gap-2 FeedbackWidget__footer">
            <BTButton data-testid="feedback-cancel-button" type="secondary" onClick={onClose}>
                Cancel
            </BTButton>
            <BTPopover content={!isValid ? "Please fill out all required fields." : undefined}>
                <BTButton
                    data-testid="feedback-submit-button"
                    type="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isValid}
                >
                    Submit feedback
                </BTButton>
            </BTPopover>
        </div>
    );

    return (
        <BTModal
            visible
            beforeClose={onClose}
            width="480px"
            data-testid="feedback-modal"
            footer={feedbackFooter}
            removeHeader
            removeBodyPadding
            title="In-App Feedback"
        >
            <BdsContentHeader
                title="Tell us what you think"
                icon={<IconGiveFeedback size="lg" />}
                testid="feedback-modal-header"
                className="FeedbackWidget__header"
                slotTop={
                    <BdsActionBar grow testid="bds-action-bar-header-top">
                        <BdsButtonGroup
                            displayType="tertiary"
                            id="bds-button-group-header-top"
                            menuTestid="bds-menu-top"
                            rollupTestid="bds-rollup-top"
                            testid="bds-button-group-header-top"
                            grow
                        >
                            <BdsButton
                                displayType="tertiary"
                                hotkey="esc"
                                icon={<IconX size="md" />}
                                id="closeButton"
                                testid="close"
                                tooltip="Close"
                                value="close"
                                onClick={onClose}
                                ariaLabel="close"
                            />
                        </BdsButtonGroup>
                    </BdsActionBar>
                }
            />
            <div className="FeedbackWidget__content">
                <BTText size="normal-md">
                    {props.feedbackText
                        ? props.feedbackText
                        : "We're improving this area and want your feedback. Share what worked, what didn't, or what you expected to see."}
                </BTText>

                {ratingMode !== "hidden" && (
                    <div className="FeedbackWidget__rating">
                        <Rate
                            value={values.rating}
                            onChange={(value) => setFieldValue("rating", value)}
                            character={
                                <>
                                    <StarOutlined className="FeedbackWidget__star-outlined" />
                                    <StarFilled className="FeedbackWidget__star-filled" />
                                </>
                            }
                        />
                        {values.rating > 0 && (
                            <BTText size="normal-md" className="FeedbackWidget__rating-label">
                                {`${values.rating} out of 5 stars`}
                            </BTText>
                        )}
                    </div>
                )}

                <div className="FeedbackWidget__feedback">
                    <BTText size="distinct-sm">Your feedback</BTText>
                    <BTTextArea
                        id="feedbackText"
                        data-testid="feedback-textarea"
                        className="FeedbackWidget__textarea"
                        value={values.feedbackText}
                        onChange={(_, value: string) => setFieldValue("feedbackText", value)}
                        onBlur={setFieldTouched}
                        debouncingContext={{ useDebouncing: false }}
                        rows={4}
                        minRows={4}
                        maxLength={1000}
                    />
                </div>
            </div>
        </BTModal>
    );
}

const FeedbackWidgetValidationSchema = (ratingMode: "hidden" | "optional" | "required") =>
    yup.object().shape<IFeedbackWidgetFormValues>({
        rating:
            ratingMode === "required"
                ? yup.number().min(1, "Rating is required").required().label("Rating")
                : yup.number().notRequired().label("Rating"),
        feedbackText: yup
            .string()
            .required("Feedback is required")
            .test("not-empty", "Feedback cannot be empty", (value: string | undefined) => {
                return typeof value === "string" && value.trim().length > 0;
            })
            .max(1000, "Maximum 1000 characters")
            .label("Your feedback"),
    });

export default withFormikBT<IFeedbackWidgetProps, IFeedbackWidgetFormValues>({
    mapPropsToValues: () => ({
        rating: 0,
        feedbackText: "",
    }),
    validationSchema: (props: IFeedbackWidgetProps) =>
        FeedbackWidgetValidationSchema(props.ratingMode ?? "optional"),
    validateOnChange: true,
    validateOnBlur: true,
    handleSubmit: (_values, _formikBag) => {
        // submission handled in the Submit button onClick
    },
})(FeedbackWidgetInternal);
